import {
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  useRef,
  createContext,
} from 'react';
import {
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
  User,
  type JwtClaims,
} from 'oidc-client-ts';
import { jwtDecode } from 'jwt-decode';
import { useCadConfig } from './cad-config';
import type { CadConfig } from './cad-config';
import {
  appendCadHandoffToUrl,
  consumeCadHandoffFromUrl,
  getCadCallbackOrigin,
  getCadRedirectUri,
  resolveCadReturnTo,
  type CadTokenBundle,
} from './cad-callback-origin';

export { CAD_LOAD_CALLBACK_PATH } from './cad-callback-origin';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface CadAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  accessToken: string | undefined;
  signinRedirect: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Attempt to refresh the access token. Returns true if successful. */
  refreshToken: () => Promise<boolean>;
  /**
   * Current access token from oidc-client-ts, refreshed first if the stored
   * User is expired (validate-before-use via User.expired — no manual parsing).
   */
  getAccessToken: () => Promise<string>;
  /** Clear the stored oidc-client-ts session (used before a forced re-auth). */
  clearSession: () => Promise<void>;
}

const CadAuthContext = createContext<CadAuthState>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: undefined,
  signinRedirect: async () => {},
  signOut: async () => {},
  refreshToken: async () => false,
  getAccessToken: async () => '',
  clearSession: async () => {},
});

export function useCadAuth(): CadAuthState {
  return useContext(CadAuthContext);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// The CAD authorization code is itself a JWT. We must read its `iss` BEFORE the
// token exchange to target the tenant token endpoint, and oidc-client-ts cannot
// parse a pre-exchange code (no User yet) — so jwt-decode is used here, typed by
// oidc-client-ts's own JwtClaims. This is the ONLY raw decode; every token/claim
// after exchange comes from the oidc-client-ts User/profile.
function parseJwtPayload(jwt: string): JwtClaims | undefined {
  try {
    return jwtDecode<JwtClaims>(jwt);
  } catch {
    return undefined;
  }
}

function extractIssuerOrigin(jwt: string): string | undefined {
  const claims = parseJwtPayload(jwt);
  if (!claims?.iss) return undefined;
  try {
    return new URL(claims.iss).origin;
  } catch {
    return undefined;
  }
}

/**
 * Where oidc-client-ts persists the OIDC user (the access/refresh/id tokens).
 * We use localStorage so the session is shared across all tabs on the same
 * origin (GLOB-1386): the token IS the session — CAD is stateless, the backend
 * validates the bearer JWT on every request and keeps no server-side session —
 * so a second tab (or a link opened in a new tab) inherits the existing session
 * instead of being forced to re-authenticate. Expiry is handled reactively
 * (signinSilent on a 401 and on startup when the stored token is expired), so a
 * stale token persisted across a browser restart simply triggers a silent
 * refresh on next use.
 *
 * IMPORTANT: this constant is only ever handed to oidc-client-ts via the
 * `userStore`/`stateStore` below. We never read the `oidc.user:*` record out of
 * it directly — the access token is always obtained from the oidc-client-ts
 * `User` object (`userManager.getUser()` / `signinSilent()` / the userLoaded
 * event). Parsing the raw token out of web storage is forbidden.
 */
const CAD_STORE: Storage = localStorage;

/**
 * Issuer origin for the per-tenant token endpoint, taken from the oidc-client-ts
 * User profile (the `iss` claim oidc-client-ts already parsed from the id_token).
 * We never re-decode the access token ourselves — oidc-client-ts owns parsing.
 */
function issuerOriginFromUser(user: User | null | undefined): string | undefined {
  const iss = user?.profile?.iss;
  if (!iss) return undefined;
  try {
    return new URL(iss).origin;
  } catch {
    return undefined;
  }
}

/**
 * Post a `cad-auth-ready` message to the WebView2 host (if present) with the
 * current user's token bundle. Used by the desktop Review shell to capture
 * tokens whether they arrive via fresh callback, an existing stored session, or
 * silent refresh — without this, a WebView2 launched against an already-
 * authenticated Load app would never tell the host it's signed in.
 */
function postCadAuthReadyIfWebView2(u: User): void {
  const wv = (
    window as unknown as { chrome?: { webview?: { postMessage?: (m: string) => void } } }
  ).chrome?.webview;
  if (!wv?.postMessage) return;
  if (!u.access_token?.trim()) return;
  const bundle = {
    accessToken: u.access_token,
    refreshToken: u.refresh_token,
    idToken: u.id_token,
    expiresAt: u.expires_at,
    profile: u.profile,
    issuer: issuerOriginFromUser(u),
  };
  wv.postMessage(JSON.stringify({ type: 'cad-auth-ready', ...bundle }));
}

const CAD_RETURN_TO_KEY = 'cad.oidc.returnTo';
const CAD_RETURN_ORIGIN_KEY = 'cad.oidc.returnOrigin';

function parseOidcState(state: unknown): { returnTo?: string; returnOrigin?: string } | null {
  if (state == null) return null;
  if (typeof state === 'object') {
    return state as { returnTo?: string; returnOrigin?: string };
  }
  if (typeof state === 'string') {
    try {
      return JSON.parse(state) as { returnTo?: string; returnOrigin?: string };
    } catch {
      return null;
    }
  }
  return null;
}

/** Token host for code exchange — JWT iss from auth code or stored user, else cadLoginUrl. */
async function resolveTenantHost(userManager: UserManager, loginBase: string): Promise<string> {
  const code = new URLSearchParams(window.location.search).get('code');
  if (code) {
    const origin = extractIssuerOrigin(code);
    if (origin) return origin;
  }
  // Use the oidc-client-ts User: the issuer comes from the profile (id_token
  // `iss`) that oidc-client-ts already parsed — we don't re-decode the token.
  const stored = await userManager.getUser();
  const origin = issuerOriginFromUser(stored);
  if (origin) return origin;
  return loginBase.replace(/\/$/, '');
}

async function applyTenantMetadata(userManager: UserManager, tokenHost: string): Promise<void> {
  const meta = await userManager.metadataService.getMetadata();
  meta.issuer = `${tokenHost}/v2`;
  meta.authorization_endpoint = `${tokenHost}/oauth2/v2/authorize`;
  meta.token_endpoint = `${tokenHost}/oauth2/v2/token`;
  meta.userinfo_endpoint = `${tokenHost}/oauth2/user-info`;
  meta.end_session_endpoint = `${tokenHost}/logout`;
  meta.jwks_uri = `${tokenHost}/v2/.well-known/openid-configuration/jwks`;
}

/**
 * Resolve the tenant/token host (the JWT `iss` origin) for the seed metadata —
 * the same approach as Vue's cadAuth `resolveTenantHost` — so the OIDC client is
 * configured with the token-issuing host (e.g. `certara.dev.certara.net`) as its
 * issuer/token base, not the cadLoginUrl portal (`my.*.certara.net`).
 *
 * Synchronous because buildOidcSettings runs before any UserManager exists:
 * read the auth code from the URL (the code is a JWT), else fall back to
 * cadLoginUrl. Existing users are read through UserManager.getUser() during
 * initialization, then their tenant metadata is applied there.
 */
function resolveSeedTokenHost(loginBase: string): string {
  const code = new URLSearchParams(window.location.search).get('code');
  if (code) {
    const origin = extractIssuerOrigin(code);
    if (origin) return origin;
  }
  return loginBase;
}

function buildOidcSettings(config: CadConfig): UserManagerSettings {
  const loginBase = config.loginUrl.replace(/\/$/, '');
  const tokenHost = resolveSeedTokenHost(loginBase);
  const redirectUri = getCadRedirectUri();

  return {
    authority: loginBase,
    client_id: config.clientId,
    redirect_uri: redirectUri,
    // No post_logout_redirect_uri: CAD rejects arbitrary post-logout returns.
    response_type: 'code',
    scope: config.scopes,
    // Refresh is reactive only by design: signinSilent() runs on a 401 (retried
    // by the API client) and on startup when the stored token is expired. CAD
    // access tokens are short-lived (~15 min), but we deliberately do NOT enable
    // automaticSilentRenew — no proactive background timer; we refresh on demand
    // when a request actually fails with 401.
    automaticSilentRenew: false,
    silent_redirect_uri: redirectUri,
    // Fetch /userinfo after code exchange and merge its claims into
    // user.profile. CAD's id_token only carries sub/uid/aud/iss/exp/iat/email —
    // the display-name claims (name / given_name / family_name /
    // preferred_username) live on the userinfo endpoint. Enabling this makes
    // oidc-client-ts the single owner of user info, so the UI can read the real
    // name from useAuth().user.profile instead of falling back to the email.
    loadUserInfo: true,
    // localStorage owns the whole session: the access/refresh tokens
    // (userStore) and the transient authorize/PKCE state (stateStore). Using
    // localStorage (origin-scoped, shared across tabs) is what lets a second
    // tab inherit the session instead of re-authenticating (GLOB-1386).
    // Authorize→callback share one browser context (and one WebView2 context on
    // desktop), so the stateStore round-trips correctly; signinSilent uses the
    // refresh_token grant, not an iframe.
    userStore: new WebStorageStateStore({ store: CAD_STORE }),
    stateStore: new WebStorageStateStore({ store: CAD_STORE }),
    // Provide full metadata to avoid CORS-blocked discovery fetch. The
    // cadLoginUrl `/v2/.well-known/openid-configuration` endpoint does not
    // emit CORS headers, so a browser fetch from a different origin (e.g.
    // the localhost dev server) is blocked. Token-side endpoints are seeded
    // from the tenant/token host (resolveSeedTokenHost) and re-applied
    // per-tenant in the callback handler (applyTenantMetadata).
    metadata: {
      // Token-side endpoints use the tenant/token host (the JWT `iss`), matching
      // Vue's cadAuth: id_token issuer validation, token exchange, userinfo and
      // JWKS all target the issuing host (e.g. certara.dev.certara.net), not the
      // cadLoginUrl portal. authorize/end_session stay on the portal (loginBase),
      // where org SAML routing and logout live.
      issuer: `${tokenHost}/v2`,
      authorization_endpoint: `${loginBase}/oauth2/v2/authorize`,
      token_endpoint: `${tokenHost}/oauth2/v2/token`,
      // userinfo lives at /oauth2/user-info (NOT /oauth2/v2/userinfo), per the
      // CAD .well-known/openid-configuration discovery doc.
      userinfo_endpoint: `${tokenHost}/oauth2/user-info`,
      // RP-initiated logout lives at `/logout` (NOT `/oauth2/v2/logout`), per
      // the CAD discovery doc — the `/oauth2/v2/logout` path 404s.
      end_session_endpoint: `${loginBase}/logout`,
      jwks_uri: `${tokenHost}/v2/.well-known/openid-configuration/jwks`,
    },
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

function CadOidcProvider({
  config,
  baseUrl,
  children,
}: {
  config: CadConfig;
  baseUrl: string;
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  // True once this tab is signing out (user-initiated or cross-tab); makes
  // signinRedirect a no-op so nothing re-authenticates a tab that is leaving.
  const signingOutRef = useRef(false);

  const settings = useMemo(() => buildOidcSettings(config), [config]);
  const userManager = useMemo(() => new UserManager(settings), [settings]);

  // Initialize: check for existing session or handle callback
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      // Cross-port dev handoff: Load callback redirected here with #cad_auth=…
      // Restore the exact oidc-client-ts User the Load callback serialized — no
      // hand-built User, no field reconstruction. oidc-client-ts owns/persists it.
      const handoffUserStorage = consumeCadHandoffFromUrl();
      if (handoffUserStorage) {
        const handoffUser = User.fromStorageString(handoffUserStorage);
        await userManager.storeUser(handoffUser);
        // Wire token-side metadata to the tenant host so a later reactive
        // (401) refresh hits the right /token endpoint.
        const handoffOrigin = issuerOriginFromUser(handoffUser);
        if (handoffOrigin) await applyTenantMetadata(userManager, handoffOrigin);
        setUser(handoffUser);
        setIsLoading(false);
        return;
      }

      const callbackParams = new URLSearchParams(window.location.search);
      const callbackCode = callbackParams.get('code');
      const callbackState = callbackParams.get('state');

      // Sibling SPA (:8087/:8088) must not call signinRedirect locally — OIDC state
      // is stored per-origin; the callback always lands on Load (:8086).
      const cadLoginParams = new URLSearchParams(window.location.search);
      if (cadLoginParams.get('cad_login') === '1') {
        const returnTo =
          cadLoginParams.get('returnTo') ??
          sessionStorage.getItem(CAD_RETURN_TO_KEY) ??
          window.location.href;
        const returnOrigin =
          cadLoginParams.get('returnOrigin') ??
          sessionStorage.getItem(CAD_RETURN_ORIGIN_KEY) ??
          window.location.origin;
        sessionStorage.setItem(CAD_RETURN_TO_KEY, returnTo);
        sessionStorage.setItem(CAD_RETURN_ORIGIN_KEY, returnOrigin);
        window.history.replaceState(null, '', window.location.pathname || '/');
        try {
          await userManager.signinRedirect({
            state: { returnTo, returnOrigin },
          });
        } catch (err) {
          console.error('[CAD] cad_login signinRedirect failed:', err);
        }
        setIsLoading(false);
        return;
      }

      if (callbackCode && callbackState) {
        try {
          const loginBase = config.loginUrl.replace(/\/$/, '');
          const tokenHost = await resolveTenantHost(userManager, loginBase);
          await applyTenantMetadata(userManager, tokenHost);

          const u = await userManager.signinRedirectCallback(window.location.href);
          if (!u.access_token?.trim()) {
            throw new Error('[CAD] signinRedirectCallback returned no access_token');
          }
          setUser(u);

          const wv = (
            window as unknown as { chrome?: { webview?: { postMessage?: (m: string) => void } } }
          ).chrome?.webview;
          if (wv?.postMessage) {
            // Desktop WebView2 host: post the flat camelCase token fields its C#
            // handler (LogOnPresenter.CadAuthMessage) consumes. It cannot read
            // the oidc-client-ts storage string, so this path keeps the bundle.
            const desktopBundle: CadTokenBundle = {
              accessToken: u.access_token,
              refreshToken: u.refresh_token,
              idToken: u.id_token,
              issuer: issuerOriginFromUser(u),
            };
            wv.postMessage(JSON.stringify({ type: 'cad-auth-ready', ...desktopBundle }));
          } else {
            // SPA sibling (Security/WebReview on another dev port): hand over the
            // exact serialized User so it restores verbatim via fromStorageString.
            const oidcState = parseOidcState(u.state);
            const returnTo = resolveCadReturnTo(
              oidcState?.returnTo ?? sessionStorage.getItem(CAD_RETURN_TO_KEY) ?? undefined,
              baseUrl,
              oidcState?.returnOrigin ?? sessionStorage.getItem(CAD_RETURN_ORIGIN_KEY) ?? undefined
            );
            sessionStorage.removeItem(CAD_RETURN_TO_KEY);
            sessionStorage.removeItem(CAD_RETURN_ORIGIN_KEY);
            const target = appendCadHandoffToUrl(returnTo, u.toStorageString());
            window.location.replace(target);
          }
        } catch (err) {
          console.error('[CAD] Login callback error:', err);
        }
        setIsLoading(false);
        return;
      }

      // Restore an existing session. Auth state is the PRESENCE of a User from
      // oidc-client-ts (getUser) — we don't inspect the access token. We do NOT
      // proactively check or refresh on expiry here; expiration is handled
      // reactively by the API client's 401 → refresh → retry, so an
      // expired-but-present token still counts as signed in.
      const existing = await userManager.getUser();
      if (existing) {
        // Point the token-side metadata at the tenant host so the reactive (401)
        // refresh hits the right /token endpoint (the seed metadata uses the
        // cadLoginUrl portal).
        const existingOrigin = issuerOriginFromUser(existing);
        if (existingOrigin) await applyTenantMetadata(userManager, existingOrigin);
        setUser(existing);
        postCadAuthReadyIfWebView2(existing);
      }
      // No session — ProtectedRoute will redirect to login.
      setIsLoading(false);
    };

    init();
  }, [userManager]);

  // Listen for token changes (silent renew, etc.)
  useEffect(() => {
    const onUserLoaded = (u: User) => {
      setUser(u);
      postCadAuthReadyIfWebView2(u);
    };
    const onUserUnloaded = () => setUser(null);

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, [userManager]);

  // Refresh via the OIDC refresh_token grant (signinSilent). Exposed publicly
  // and called reactively on a 401 (the API client retries once on success).
  // Concurrent callers share one in-flight refresh so a burst of 401s triggers
  // exactly one token exchange. The tenant token endpoint is already wired into
  // the metadata at callback / session-restore time, so no per-call fixup here.
  const refreshToken = useCallback((): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const run = async (): Promise<boolean> => {
      try {
        const refreshed = await userManager.signinSilent();
        if (refreshed) {
          setUser(refreshed);
          console.log('[CAD] Token refreshed successfully');
          return true;
        }
      } catch (err) {
        console.warn('[CAD] Token refresh failed:', err);
      }
      return false;
    };
    const p = run().finally(() => {
      refreshPromiseRef.current = null;
    });
    refreshPromiseRef.current = p;
    return p;
  }, [userManager]);

  // Validate-before-use: read the User from oidc-client-ts and, if it reports
  // itself expired (User.expired — oidc-client-ts's own check, no manual JWT
  // parsing), refresh silently first and return the fresh token. When the token
  // has no known expiry (CAD omits expires_in → expires_at undefined → .expired
  // undefined) this skips the proactive refresh and the reactive 401 → refresh →
  // retry path covers it.
  const getAccessToken = useCallback(async (): Promise<string> => {
    let current = await userManager.getUser();
    if (current?.expired) {
      const ok = await refreshToken();
      if (ok) current = await userManager.getUser();
    }
    return current?.access_token?.trim() ?? '';
  }, [userManager, refreshToken]);

  // Clear the stored session so a forced re-auth starts clean and a bad token
  // isn't re-sent on the next load. Both calls are defensive: clearStaleState
  // must run even if removeUser throws.
  const clearSession = useCallback(async () => {
    try {
      await userManager.removeUser();
    } catch (err) {
      console.warn('[CAD] removeUser during clearSession failed:', err);
    }
    try {
      await userManager.clearStaleState();
    } catch (err) {
      console.warn('[CAD] clearStaleState during clearSession failed:', err);
    }
  }, [userManager]);

  const signinRedirect = useCallback(async () => {
    // A signout is navigating this tab away. clearSession() during signOut
    // raises userUnloaded → setUser(null), which re-renders ProtectedRoute
    // into auth.login() — without this guard that signinRedirect races the
    // in-flight signoutRedirect and can silently sign the tab back in
    // (observed in testing: the authorize round-trip beat the /logout).
    if (signingOutRef.current) return;
    sessionStorage.setItem(CAD_RETURN_TO_KEY, window.location.href);
    sessionStorage.setItem(CAD_RETURN_ORIGIN_KEY, window.location.origin);

    const callbackOrigin = getCadCallbackOrigin();
    if (window.location.origin !== callbackOrigin) {
      const start = new URL('/', callbackOrigin);
      start.searchParams.set('cad_login', '1');
      start.searchParams.set('returnTo', window.location.href);
      start.searchParams.set('returnOrigin', window.location.origin);
      window.location.assign(start.toString());
      return;
    }

    await userManager.signinRedirect({
      state: { returnTo: window.location.href, returnOrigin: window.location.origin },
    });
  }, [userManager]);

  const signOut = useCallback(async () => {
    signingOutRef.current = true;
    // Capture id_token before clearing storage — signoutRedirect needs it as
    // id_token_hint to properly end the IdP session server-side. Treat storage
    // failures as best-effort so they cannot prevent session cleanup/logout.
    let idTokenHint: string | undefined;
    try {
      idTokenHint = (await userManager.getUser())?.id_token;
    } catch (err) {
      console.warn('[CAD] getUser during signOut failed:', err);
    }
    await clearSession();
    await userManager.signoutRedirect(idTokenHint ? { id_token_hint: idTokenHint } : undefined);
  }, [userManager, clearSession]);

  // Cross-tab logout (GLOB-1424): all same-origin GS tabs share one
  // oidc-client-ts user record in localStorage, so the removeUser() in the tab
  // that signs out fires a `storage` event (newValue === null) in every other
  // tab. oidc-client-ts does not expose its internal user-storage key, so on a
  // removal we ask UserManager whether its user disappeared instead of
  // duplicating the private `oidc.user:{authority}:{client_id}` format. Writes
  // and removals of unrelated records are ignored.
  const hasUser = user !== null;
  useEffect(() => {
    if (!hasUser) return;

    let signoutTimer: ReturnType<typeof setTimeout> | undefined;
    const handler = (event: StorageEvent) => {
      if (event.newValue !== null || event.oldValue === null || signingOutRef.current) {
        return;
      }

      void userManager
        .getUser()
        .then(storedUser => {
          if (storedUser || signingOutRef.current) return;

          // This tab is leaving; block any competing signinRedirect (e.g. a
          // 401 recovery) from re-authenticating it in the meantime.
          signingOutRef.current = true;
          // Let the initiating tab reach the end-session endpoint first.
          signoutTimer = setTimeout(() => {
            void userManager.signoutRedirect().catch(err => {
              console.warn('[CAD] Cross-tab signout failed:', err);
              window.location.assign(
                new URL('/login?noredirect', userManager.settings.authority).toString()
              );
            });
          }, 1500);
        })
        .catch(err => console.warn('[CAD] Cross-tab session check failed:', err));
    };

    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
      if (signoutTimer !== undefined) clearTimeout(signoutTimer);
    };
  }, [hasUser, userManager]);

  // Listen for inbound host→web messages from the WebView2 desktop shell.
  // The desktop posts { type: 'cad-signout' } to trigger the same signOut()
  // flow as clicking Sign Out in the UI: removeUser/clearStaleState + signoutRedirect.
  useEffect(() => {
    const wv = (
      window as unknown as { chrome?: { webview?: { addEventListener?: (type: string, fn: (e: MessageEvent) => void) => void; removeEventListener?: (type: string, fn: (e: MessageEvent) => void) => void; postMessage?: (m: string) => void } } }
    ).chrome?.webview;
    if (!wv?.addEventListener) return;

    const handler = (e: MessageEvent) => {
      try {
        const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (msg?.type === 'cad-signout') signOut();
      } catch {
        // ignore malformed messages
      }
    };

    wv.addEventListener('message', handler);
    // Signal to the WebView2 host that the listener is set up and ready to
    // receive commands — the host waits for this before posting 'cad-signout'.
    wv.postMessage?.(JSON.stringify({ type: 'cad-webview-listener-ready' }));
    return () => wv.removeEventListener?.('message', handler);
  }, [signOut]);

  const value = useMemo<CadAuthState>(
    () => ({
      // Signed in iff oidc-client-ts holds a User (the standard OIDC signal,
      // i.e. getUser() !== null, surfaced here as the `user` state). We do NOT
      // inspect the access token or gate on expiry — expiration is handled
      // reactively by the API client's 401 → refresh → retry.
      isAuthenticated: hasUser,
      isLoading,
      user,
      accessToken: user?.access_token,
      signinRedirect,
      signOut,
      refreshToken,
      getAccessToken,
      clearSession,
    }),
    [hasUser, user, isLoading, signinRedirect, signOut, refreshToken, getAccessToken, clearSession]
  );

  return <CadAuthContext.Provider value={value}>{children}</CadAuthContext.Provider>;
}

/**
 * CadAuthProvider reads cad config from CadConfigProvider context.
 * If CAD is enabled, provides OIDC auth via oidc-client-ts UserManager.
 * If CAD is disabled, renders children without any auth wrapper.
 */
export function CadAuthProvider({ children }: { children: ReactNode }) {
  const config = useCadConfig();
  const baseUrl = (typeof window !== 'undefined' && (window as any).baseUrl) || '/';

  if (!config.enabled) {
    return <>{children}</>;
  }

  return (
    <CadOidcProvider config={config} baseUrl={baseUrl}>
      {children}
    </CadOidcProvider>
  );
}
